import { formatDataTest } from '@/common/dataTest/DataTest';
import { useDialog } from '@/common/dialog/DialogContext';
import { useInDialog } from '@/common/dialog/DialogScopeContext';
import { EntitySelectDialog } from '@/common/dialog/EntitySelectDialog';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useNavState } from '@/common/navState/NavStateContext';
import { StringUtils } from '@/common/utils/StringUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormControl, FormHelperText, IconButton, Input, InputAdornment, InputLabel } from '@mui/material';
import { useMemo } from 'react';
import { useForm } from '../FormContext';
import { useFormFieldDef } from '../hooks/useFormFieldDef';


export const EntitySelect = ({ fieldDef, iconButton }) => {
  const { formValues, assignValue } = useForm();
  const { onBlur, error, helperText, label, disabled } = useFormFieldDef(fieldDef);
  const { openDialog } = useDialog();
  const { navigateTo } = useNavState();
  const inDialog = useInDialog();

  // Object used internally to track id, code, and name for display purposes
  const idCodeName = useMemo(() => {
    // Special handling if this field is 'code' vs a FK Entity
    const isCode = fieldDef.field === 'code'

    const idField = isCode ? 'id' : fieldDef.field;
    if (!formValues[idField]) return null;

    if (isCode) {
      const { id, code, name } = formValues;
      return { id, code, name };
    }

    return {
      id: formValues[fieldDef.field],
      code: formValues[fieldDef.field.replace('Id', 'Code')],
      name: formValues[fieldDef.field.replace('Id', 'Name')],
    }
  }, [formValues])

  // Handle click of the icon button
  const handleClick = (event) => {
    if (disabled) { // If disabled, this means the user navigates on click
      navigateToEntity(event.ctrlKey);
      return;
    }

    // If there is already a value, then clicking the button clears the current selection
    if (idCodeName) {
      assignFormValues({});
      return;
    }

    // If there is no selection, open the EntitySelectDialog
    openEntitySelectDialog();
  }

  const navigateToEntity = (openNewTab) => {
    const route = fieldDef.module + '/' + StringUtils.toKebabConvention(fieldDef.entity);
    const navState = {
      entityId: idCodeName.id,
      mode: Mode.DETAIL,
    }
    navigateTo(route, navState, openNewTab)
  }

  const openEntitySelectDialog = () => {
    openDialog(EntitySelectDialog, {
      data: {
        module: fieldDef.module,
        entity: fieldDef.entity,
        rowSelection: fieldDef.rowSelection,
        field: fieldDef.field,
        expr: fieldDef.expr,
        query: fieldDef.query,
        replaceParams: fieldDef.replaceParams
        // TODO: clientSideFetchRowData?
      }
    }).then((selectedEntities) => {
      onBlur();
      if (selectedEntities == null || selectedEntities.length === 0) {
        return;
      }
      assignFormValues(selectedEntities[0]);
    })
  }

  // Assign id, code, and name to the formValues
  const assignFormValues = (obj) => {
    assignValue(fieldDef.field, obj.id ?? null);
    assignValue(fieldDef.field.replace('Id', 'Code'), obj.code ?? null);
    assignValue(fieldDef.field.replace('Id', 'Name'), obj.name ?? null);
  }

  // Helper function to provide the text displayed in the input
  const inputText = () => {
    if (!idCodeName) {
      return '';
    }
    if (idCodeName.name) {
      return `${idCodeName.name} (${idCodeName.code})`;
    }
    return idCodeName.code;
  }

  // Helper function to provide the icon to the IconButton
  const icon = () => {
    if (disabled) {
      return fas.faLink;
    }
    return idCodeName ? fas.faTimes : fas.faSearch;
  }

  const renderIconButton = () => {
    if (iconButton) return iconButton;

    // If disabled and no value, hide the button as we cannot navigate
    if (disabled && !idCodeName) return;

    return (
      <IconButton
        data-test-id={formatDataTest(inDialog ? 'dialog' : 'main', fieldDef.displayName, 'select-button')}
        onClick={(event) => handleClick(event)}
      >
        <FontAwesomeIcon icon={icon()} />
      </IconButton>
    )
  }

  return (
    <FormControl error={error} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Input
        data-test-id={formatDataTest(inDialog ? 'dialog' : 'main', 'form-field', fieldDef.displayName)}
        readOnly
        value={inputText()}
        endAdornment={
          <InputAdornment position="end">
            {renderIconButton()}
          </InputAdornment>
        }
      />
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  )
}